import styled from '@emotion/styled';
import React, { useEffect, useMemo, useRef } from 'react';
import { SFProduct } from '~/features/product-details-page/pdp.definitions';
import { format } from 'date-fns';
import da from 'date-fns/locale/da';
import { _transformAlgoliaToProductDetails } from '~/features/productList/Algoliga.utils';
import { Article, Modules, P160ArticlePage as P160ArticlePageType } from '~/lib/data-contract';
import { useProductHandler } from '~/services/search/useProductHandler';
import { Gutter } from '~/shared/components';
import { MaxWidth } from '~/shared/components/MaxWidth';
import Grid from '~/shared/components/Grid';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { SimpleProductList } from '~/shared/components/SimpleProductList';
import { useAlgoliaQuery } from '~/shared/hooks/useAlgoliaQuery';
import { useUtils } from '~/shared/hooks/useUtils';
import { useTranslation } from '~/shared/utils/translation';
import { DynamicBlockList } from '~/templates/blocks';
import { breakpoints } from '~/theme/breakpoints';
import Spacer from '~/theme/design-system/components/Spacer';
import { ArticleCard } from '../../P165ArticleListPage/components/Articles/Articles';
import { Text } from '~/shared/components/Text';
import {
    StyledCategory,
    StyledInfoContainer,
} from '../../P165ArticleListPage/components/Articles/styled';
import { StyledAspectRatio } from '~/shared/components/AspectRatio/styled';
import { useFrame } from '~/shared/utils';
import PageHero from '../../PageHero/PageHero';
import { GTM_LIST_ID, GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';

type P160ArticlePageProps = P160ArticlePageType;

export const P160ArticlePage = (props: P160ArticlePageProps) => {
    const { article, relatedArticles, heroImageAltText } = props;
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const ref = useRef<HTMLDivElement | null>(null);
    const { data: frame } = useFrame();

    const {
        productIds,
        productListHeader,
        pageImage,
        creationDate,
        pageTitle,
        createdBy,
        pageDescription,
        heroTheme,
    } = article;
    const getProductHandler = useProductHandler();

    const productIdsArray = useMemo(
        () => (productIds?.length ? productIds?.join(' OR id:') : undefined),
        [productIds],
    );

    const { data } = useAlgoliaQuery<{ hits: SFProduct[] }, TProductDetails[]>(
        { filters: 'id:' + productIdsArray },
        {
            queryKey: [JSON.stringify({ filters: 'id:' + productIds?.join(' OR id:') })], // Add this line
            select: (data) => {
                return data.hits.map((hit) =>
                    _transformAlgoliaToProductDetails(
                        hit,
                        translate,
                        convertPriceToLocale,
                        frame?.productSettings,
                    ),
                ) as TProductDetails[];
            },
            enabled: !!article.productIds,
        },
    );

    const articleDate = format(new Date(creationDate), 'd MMMM yyyy', { locale: da });
    const authorLine = `${translate('content.writtenBy', { author: createdBy })} · ${articleDate}`;

    const hackToRemoveImportContentHeader = () => {
        const xpath = "//p[text()='importedContent']";
        const matchingElement = document.evaluate(
            xpath,
            document,
            null,
            XPathResult.FIRST_ORDERED_NODE_TYPE,
            null,
        ).singleNodeValue;

        if (matchingElement) {
            matchingElement.parentNode?.removeChild(matchingElement);
        }
    };

    const removeAnchorLinks = () => {
        if (ref.current) {
            /** Target links with inline styles
             * This is old buttons that should not be on the page
             */
            const linksWithInlineStyle = ref.current.querySelectorAll('a[style]');
            linksWithInlineStyle.forEach((link) => {
                link.parentElement?.removeChild(link);
            });
        }
    };

    const cleanInlineParagraphs = () => {
        if (ref.current) {
            const paragraphsWithInlineStyles = ref.current.querySelectorAll('p[style]');
            paragraphsWithInlineStyles.forEach((p) => {
                p.setAttribute('style', '');
            });
        }
    };

    const cleanImportedContent = () => {
        setTimeout(() => {
            hackToRemoveImportContentHeader();
            removeAnchorLinks();
            cleanInlineParagraphs();
        }, 1000);
    };
    useEffect(() => {
        cleanImportedContent();
    }, []);

    return (
        <>
            <MaxWidth>
                <PageHero
                    image={pageImage}
                    imageAltText={heroImageAltText}
                    theme={heroTheme ?? 'Light'}
                />

                <Spacer />

                <Grid
                    flow="column"
                    breakpoints={{
                        [breakpoints.xs]: {
                            columns: '1fr',
                            flow: 'row',
                        },
                        [breakpoints.sm]: {
                            columns: '1fr',
                            flow: 'row',
                        },
                        [breakpoints.md]: {
                            columns: '2fr 1fr',
                            flow: 'column',
                        },
                    }}
                    columnGap="large"
                    rowGap="large"
                >
                    <div>
                        <Gutter gutterOnMobile gutterSize="xl">
                            <ArticleHeader
                                title={pageTitle}
                                teaser={pageDescription}
                                authorLine={authorLine}
                            />
                        </Gutter>
                        <Spacer />
                        <div ref={ref}>
                            <DynamicBlockList elements={props.pageElements as Modules[]} />
                        </div>
                        <Spacer />
                        <HideOnMobile>
                            {data && (
                                <SimpleProductList
                                    listId={GTM_LIST_ID.ARTICLE_PRODUCTS}
                                    listName={GTM_LIST_NAME.ARTICLE_LIST}
                                    variant="large"
                                    items={data}
                                    header={
                                        productListHeader ||
                                        translate('content.itemsInThisCategoryHeader')
                                    }
                                    productHandler={getProductHandler}
                                />
                            )}
                        </HideOnMobile>
                    </div>

                    <Aside>
                        {data && (
                            <SimpleProductList
                                listId={GTM_LIST_ID.ARTICLE_PRODUCTS}
                                listName={GTM_LIST_NAME.ARTICLE_LIST}
                                variant="small"
                                items={data}
                                header={
                                    productListHeader ||
                                    translate('content.itemsInThisCategoryHeader')
                                }
                                productHandler={getProductHandler}
                            />
                        )}
                        {relatedArticles && <RelatedArticles articles={relatedArticles} />}
                    </Aside>
                </Grid>
            </MaxWidth>
            <DynamicBlockList elements={props.bottomPageElements as Modules[]} />
        </>
    );
};

const RelatedArticles = styled(
    ({ articles, className }: { className?: string; articles: Article[] }) => {
        return (
            <>
                <Spacer />
                <div className={className}>
                    {/* TODO: Move text to umbraco */}
                    <Text style={{ textAlign: 'center' }} variant="display4">
                        Artikler til dig
                    </Text>
                    <Grid
                        flow="row"
                        breakpoints={{
                            [breakpoints.xs]: {
                                columns: '1fr 1fr',
                                flow: 'row',
                            },
                        }}
                    >
                        {articles &&
                            articles
                                .slice(0, 6)
                                ?.map((a) => (
                                    <ArticleCard variant="compact" article={a} priority={false} />
                                ))}
                    </Grid>
                </div>
                <Spacer />
            </>
        );
    },
)(({ theme }) => ({
    paddingTop: theme.space[4],
    [`${StyledCategory}`]: {
        padding: 0,
        paddingBottom: theme.space[2],
    },
    [`${StyledInfoContainer}`]: {
        padding: theme.space[3],
        p: {
            textOverflow: 'unset',
            overflow: 'unset',
            WebkitLineClamp: 'unset',
        },
    },
    [breakpoints.md]: {
        background: theme.colors.grey10,
        [`${StyledCategory}`]: {
            padding: theme.space[6],
            paddingBottom: theme.space[2],
        },

        [`${StyledAspectRatio}`]: {
            marginBottom: theme.space[3],
        },
    },
}));

type TArticleHeader = {
    title: string;
    teaser: string;
    authorLine: string;
    className?: string;
};
const ArticleHeader = styled(({ title, teaser, authorLine, className }: TArticleHeader) => {
    return (
        <div className={className}>
            <h1>{title}</h1>
            <p>
                <strong>{teaser}</strong>
            </p>
            <span>{authorLine}</span>
        </div>
    );
})(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.space[3],
    p: {
        fontSize: theme.fontSizes['md'],
        lineHeight: '26px',
    },
    span: {
        fontSize: theme.fontSizes['xs'],
        color: theme.colors.grey100,
    },
}));

const HideOnMobile = styled.div({
    display: 'none',
    [breakpoints.md]: {
        display: 'block',
    },
});

const Aside = styled.aside({});
